import { Filter, PartialResult } from '@empathy/search-types';
import { Observable } from 'rxjs/Observable';
import { merge } from 'rxjs/observable/merge';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { map } from 'rxjs/operators/map';
import { EVENTS } from '../../bus/events.const';
import { logger } from '../../logger';
import { EmpathyXConfig, FacetConfig, Facets, PreSelectedFilters, SearchState } from '../../models';
import { ACTIONS_KEYS, GETTERS_KEYS, MUTATIONS_KEYS } from '../../store';
import { GetFacetConfigByName } from '../../store/config/getters/facet.getter';
import { DeepPartial, Dictionary, isArrayEmptyOrNull, isObjectEmptyOrNull } from '../../utils';
import { hasNewSearchRequest } from '../pipes/has-new-search-request.operator';
import { noResultsStoredOrExpected } from '../pipes/no-results-stored-or-expected.operator';
import { requestHasQuery } from '../pipes/request-has-query.operator';
import { createWires } from '../wiring.util';
import { WIRE_FACTORY } from './wires.factory';

export const SEARCH_WIRES = createWires({
  emitDebouncedQueryChangedEvent(observable: Observable<string>, { store, emit }) {
    const { searchBox: { instantSearch: { debounceTimeInMs } } }: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
    observable.pipe(debounceTime(debounceTimeInMs)).subscribe((query: string) => {
      const { searchBox: { instantSearch } }: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
      if (instantSearch.enabled) {
        emit(EVENTS.QueryChanged, query);
      }
    });
  },
  dispatchSearch(observable, { store, emit }) {
    const searchRequest$ = observable
      .pipe(
        map(() => store.getters[GETTERS_KEYS.search.searchRequest]),
        requestHasQuery()
      );

    const nonRepeatedRequest$ = searchRequest$.pipe(hasNewSearchRequest());
    const noResultsStored$ = searchRequest$.pipe(noResultsStoredOrExpected(store));

    merge(nonRepeatedRequest$, noResultsStored$).subscribe(searchRequest => {
      store.commit(MUTATIONS_KEYS.search.setLoading, true);
      store.dispatch(ACTIONS_KEYS.search.search, searchRequest)
        .then((searchState: SearchState) => {
          store.commit(MUTATIONS_KEYS.search.setLoading, false);
          if (searchState) {
            const getFacetConfig: GetFacetConfigByName = store.getters[GETTERS_KEYS.config.facet];
            const selectedFilters: Filter[] = store.getters[GETTERS_KEYS.search.selectedFilters];
            const preselectedFilters: Facets = store.state.search.preSelectedFilters;

            if (isArrayEmptyOrNull(searchState.results) && selectedFilters.some(selectedFilter => {
              const preselectedFilter = preselectedFilters[selectedFilter.facet.modelName];
              return !getFacetConfig(selectedFilter.facet.modelName).compulsory &&
                !preselectedFilter?.filters.includes(selectedFilter);
            })) {
              emit(EVENTS.RetrySearch);
            } else {
              emit(EVENTS.SearchSuccess, searchState);
            }
          } else {
            emit(EVENTS.SearchFailed);
          }
        });
    });
  },
  emitNewQueryIfItIsNew(observable: Observable<string>, { store, emit }) {
    observable.subscribe((newQuery: string) => {
      newQuery = newQuery.toLowerCase();
      const oldQuery = store.state.search.query.toLowerCase();
      if (!oldQuery || (!oldQuery.includes(newQuery) && !newQuery.includes(oldQuery) || newQuery.length <= 2)) {
        emit(EVENTS.NewQuerySet, newQuery);
      }
    });
  },
  // This wires is to avoid the clear facets when a suggestion with facet is selected:
  //   1) If input query exists then the suggestion would be a refinement of that query and the filters will not be cleared.
  //   2) If input query is empty, it shouldn't be previous filters to clear so filters will not be cleared.
  clearFiltersIfNewQueryAndOldQueryExisted(observable: Observable<string>, { store }) {
    observable.subscribe((newQuery: string) => {
      newQuery = newQuery.toLowerCase();
      const oldQuery = store.state.search.query.toLowerCase();
      if (oldQuery && (!oldQuery.includes(newQuery) && !newQuery.includes(oldQuery) || newQuery.length <= 2)) {
        store.dispatch(ACTIONS_KEYS.search.clearFacetsFilters);
      }
    });
  },
  changeQueryIfNotHighlightedEmpathizeItem(observable, { store, emit }) {
    observable.subscribe(() => {
      const inputQuery: string = store.state.core.inputQuery;
      const empathizeHighlightedItem: any = store.getters[GETTERS_KEYS.empathize.highlightedEmpathizeItem];
      if (!empathizeHighlightedItem) {
        emit(EVENTS.QueryChanged, inputQuery);
      }
    });
  },
  loadPartialResult(observable: Observable<PartialResult>, { emit }) {
    observable.subscribe((partialResult: PartialResult) => {
      emit(EVENTS.QueryChanged, partialResult.term);
    });
  },
  changeColumnNumber(observable: Observable<number>, { store }) {
    observable.subscribe((columnNumber: number) => {
      const columnsConfig: DeepPartial<EmpathyXConfig> = { resultsGrid: { grid: { columns: columnNumber } } };
      store.commit(MUTATIONS_KEYS.config.setConfig, columnsConfig);
    });
  },
  redirectIfDirect(observable, { store, emit }) {
    observable.subscribe(() => {
      const { redirection: { autoRedirect } }: EmpathyXConfig = store.getters[GETTERS_KEYS.config.config];
      if (store.state.search.directs.length && autoRedirect) {
        emit(EVENTS.Redirection, store.state.search.directs[0]);
      }
    });
  },
  emitInfiniteScrollType(observable, { store, emit }) {
    observable.subscribe(() => {
      if (store.getters[GETTERS_KEYS.search.hasResults] && store.getters[GETTERS_KEYS.search.infiniteScrollEnabled]) {
        emit(EVENTS.InfiniteScroll);
      } else if (store.getters[GETTERS_KEYS.search.showNoResults] && store.getters[GETTERS_KEYS.noResults.infiniteScrollEnabled]) {
        emit(EVENTS.InfiniteScrollNoResults);
      } else if (store.getters[GETTERS_KEYS.search.showEmptySearch] && store.getters[GETTERS_KEYS.emptySearch.infiniteScrollEnabled]) {
        emit(EVENTS.InfiniteScrollEmptySearch);
      }
    });
  },
  syncPreselectedFiltersWithCompulsories(observable: Observable<void>, { store }) {
    observable.subscribe(
      () => store.commit(MUTATIONS_KEYS.search.setPreselectedFilters, store.getters[GETTERS_KEYS.search.compulsoryFacets])
    );
  },
  emitSearchResponseType(observable: Observable<SearchState>, { emit }) {
    observable.subscribe(newSearchState => {
      if (!isArrayEmptyOrNull(newSearchState.results)) {
        emit(EVENTS.SearchSuccessWithResults, newSearchState);
      } else if (!isArrayEmptyOrNull(newSearchState.partialResults)) {
        emit(EVENTS.SearchSuccessWithPartialResults, newSearchState);
      } else {
        emit(EVENTS.SearchSuccessWithoutResults, newSearchState);
      }
    });
  },
  checkCompulsoryFacets(observable, { store }) {
    observable
      .subscribe(() => {
        const facetsConfig: Dictionary<FacetConfig> = store.getters[GETTERS_KEYS.config.facets];
        const preselectedFilters: PreSelectedFilters = store.state.config.dynamic.filters;
        Object.entries<FacetConfig>(facetsConfig)
          .filter(([_, facetConfig]) => facetConfig.compulsory)
          .forEach(([facetName]) => {
            if (!preselectedFilters[facetName]) {
              logger.error('[FACET_CONFIG]',
                `${ facetName } is configured as compulsory, but no initial filter has been set in the EmpathyX.init(...) options`);
            }
          });
      });
  },
  search: WIRE_FACTORY.emitEvent(EVENTS.Search),
  changeQuery: WIRE_FACTORY.emitEventConditionally({ ifEvent: EVENTS.QuerySet, elseEvent: EVENTS.QueryCleared }, query => !!query),
  clearFacetsFilters: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.clearFacetsFilters),
  storeInputQuery: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.core.setInputQuery),
  storeQuery: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.setQuery),
  storeSearchState: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.saveSearchResponse),
  keepPreviousResults: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.setKeepPreviousResults, true),
  clearSearch: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.clearSearch),
  clearPage: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.clearPage),
  increasePage: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.increasePage),
  toggleFilter: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.toggleFilter),
  redirection: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.redirect),
  focusSearchInput: WIRE_FACTORY.emitEvent(EVENTS.FocusSearchInput),
  blurSearchInput: WIRE_FACTORY.emitEvent(EVENTS.BlurSearchInput),
  setFacetsGroupsFromConfig: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.setFacetsGroupsFromConfig),
  setFacetCollapse: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.setFacetCollapse),
  loadPreselectedFilters: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.loadPreselectedFilters),
  setFacetsFromPreselectedFiltersIfThereAre: WIRE_FACTORY.commitMutationConditionally(
    { ifMutation: MUTATIONS_KEYS.search.setFacetsFromPreselectedFilters },
    (_, store) => !isObjectEmptyOrNull(store.state.search.preSelectedFilters)
  ),
  resetFacetsCollapse: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.resetFacetsCollapse),
  updateFilterValue: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.updateFilterValue),
  selectFilter: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.selectFilter),
  deselectFilter: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.deselectFilter),
  setRemainingFacets: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.setRemainingFacets),
  emitQueryCleared: WIRE_FACTORY.emitEvent(EVENTS.QueryCleared),
  clearFilters: WIRE_FACTORY.dispatchAction(ACTIONS_KEYS.search.clearFacetsFilters, null),
  emitFilterSelectedType: WIRE_FACTORY.emitEventConditionally<Filter>(
    { ifEvent: EVENTS.FilterSelected, elseEvent: EVENTS.FilterSelectedNotAvailable },
    (emitedFilter, store) => {
      const compulsoryFacets = store.getters[GETTERS_KEYS.search.compulsoryFacets];
      return !!(emitedFilter.count > 0 &&
        !(compulsoryFacets[emitedFilter.facet.modelName] && emitedFilter.selected && !emitedFilter.parent));
    }),
  updateResult: WIRE_FACTORY.commitMutation(MUTATIONS_KEYS.search.updateResult)
});
